import { ReactElement, useState } from 'react';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useCourseUsgaData from '../../dataHooks/useCourseUsgaData';
import useCourseUsgaMutators from '../../dataMutators/useCourseUsgaMutators';
import Course from '../../types/Course';
import { CourseTee } from '../../types/Course';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { guessTeeColor } from '../../utils/teeColorUtils';
import { extractGenderFields } from '../../utils/usgaCourseUtils';
import { generateFirestoreId } from '../../utils/utils';

import CourseTeeListView from './CourseTeeListView';

interface CourseUsgaTeeViewProps {
  courseId: string;
  course?: Course;
  isAdmin?: boolean;
  /** Routes tee writes through the same save pipeline as CourseTeeEditor, so the two
   *  UIs never independently write the shared `tees` field and race each other. */
  onTeeDataChange?: (updates: Partial<Course>) => void;
}

function arraysEqual(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export default function CourseUsgaTeeView({ courseId, course, isAdmin, onTeeDataChange }: CourseUsgaTeeViewProps): ReactElement {
  const { usgaTees, ghinCourseId, isUsgaDataLoading } = useCourseUsgaData(courseId);
  const { updateUsgaTees } = useCourseUsgaMutators();
  const [importingTeeId, setImportingTeeId] = useState<string | null>(null);
  const [confirmYardageTeeId, setConfirmYardageTeeId] = useState<string | null>(null);

  if (isUsgaDataLoading) {
    return <CircularProgress size="1.5rem" />;
  }

  if (!usgaTees || usgaTees.length === 0) {
    return (
      <Typography variant="bodyMedium" color="text.secondary">
        No USGA tee data imported yet.
      </Typography>
    );
  }

  // Find a local tee whose name and per-hole par match but yardage differs.
  const findYardageConflict = (usgaTee: CourseTee): CourseTee | undefined =>
    course?.tees?.find(
      (t) =>
        t.name.toLowerCase() === usgaTee.name.toLowerCase() &&
        arraysEqual(t.par ?? [], usgaTee.par ?? []) &&
        !arraysEqual(t.yardage ?? [], usgaTee.yardage ?? [])
    );

  // Local tee already linked to this USGA entry — via the stored localTeeId
  // (stable across re-imports) or the back-reference fields set after the first import.
  const findLinkedTee = (usgaTee: CourseTee): CourseTee | undefined =>
    course?.tees?.find(
      (t) =>
        t.itemId === usgaTee.localTeeId ||
        t.usgaMaleTeeId === usgaTee.itemId ||
        t.usgaFemaleTeeId === usgaTee.itemId
    );

  const handleImportTee = async (usgaTee: CourseTee, yardageConflictTarget?: CourseTee) => {
    if (!course || !isAdmin) return;

    setImportingTeeId(usgaTee.itemId);
    try {
      // Find existing local tee already linked to this USGA entry.
      const existingLinkedTee = findLinkedTee(usgaTee);

      // If not already linked, look for a merge candidate:
      // same name + identical hole-by-hole yardage and par (tees are not gendered —
      // both genders' ratings live on the same physical tee object)
      const mergeCandidate = !existingLinkedTee && !yardageConflictTarget
        ? course.tees?.find((t) => {
            if (t.name.toLowerCase() !== usgaTee.name.toLowerCase()) return false;
            if (!arraysEqual(t.yardage ?? [], usgaTee.yardage ?? [])) return false;
            if (!arraysEqual(t.par ?? [], usgaTee.par ?? [])) return false;
            return true;
          })
        : undefined;

      // If no match found and not already in a confirmed yardage-conflict flow,
      // check whether name + par match with differing yardage and prompt the user.
      if (!existingLinkedTee && !mergeCandidate && !yardageConflictTarget) {
        if (findYardageConflict(usgaTee)) {
          setConfirmYardageTeeId(usgaTee.itemId);
          return;
        }
      }

      // An already-linked tee whose hole-by-hole yardage no longer matches the fresh
      // USGA data (e.g. it was linked back when USGA/GHIN data was incomplete) needs the
      // same confirmation — otherwise "Update" silently keeps stale/partial yardage forever.
      if (existingLinkedTee && !yardageConflictTarget && !arraysEqual(existingLinkedTee.yardage ?? [], usgaTee.yardage ?? [])) {
        setConfirmYardageTeeId(usgaTee.itemId);
        return;
      }

      const targetTee = existingLinkedTee ?? mergeCandidate ?? yardageConflictTarget;
      const localTeeId = targetTee?.itemId ?? usgaTee.localTeeId ?? generateFirestoreId();
      const sortOrder = targetTee?.sortOrder ?? (course.tees?.length ?? 0);

      const genderFields = extractGenderFields(usgaTee);

      // Only guess color for brand-new tees (no existing target to preserve)
      const colorGuess = targetTee ? {} : guessTeeColor(usgaTee.name);

      const importedTee: CourseTee = {
        // Base: existing local tee (merge/update) or USGA tee (new import, for shared fields)
        ...(targetTee ?? usgaTee),
        // When the user confirmed a yardage-conflict merge, refresh every hole-indexed
        // array together — yardage, par, and stroke index all move as a set.
        ...(yardageConflictTarget
          ? {
              yardage: usgaTee.yardage,
              par: usgaTee.par,
              mensStrokeIndex: usgaTee.mensStrokeIndex,
              womensStrokeIndex: usgaTee.womensStrokeIndex,
            }
          : {}),
        ...colorGuess,
        ...genderFields,
        itemId: localTeeId,
        sortOrder,
        status: 'ACTIVE',
      };
      delete importedTee.localTeeId;

      const updatedLocalTees = targetTee
        ? (course.tees ?? []).map((t) => (t.itemId === localTeeId ? importedTee : t))
        : [...(course.tees ?? []), importedTee];

      onTeeDataChange?.({ tees: updatedLocalTees });

      const updatedUsgaTees = usgaTees.map((t) =>
        t.itemId === usgaTee.itemId ? { ...t, localTeeId } : t
      );
      await updateUsgaTees(course.itemId, updatedUsgaTees);
      setConfirmYardageTeeId(null);
    } finally {
      setImportingTeeId(null);
    }
  };

  const renderImportAction = (usgaTee: CourseTee) => {
    if (!isAdmin || !course) return null;

    const isImporting = importingTeeId === usgaTee.itemId;
    const isLinked = !!usgaTee.localTeeId ||
      !!course.tees?.find((t) => t.usgaMaleTeeId === usgaTee.itemId || t.usgaFemaleTeeId === usgaTee.itemId);

    if (isImporting) {
      return <CircularProgress size="1rem" />;
    }

    if (confirmYardageTeeId === usgaTee.itemId) {
      const conflict = findLinkedTee(usgaTee) ?? findYardageConflict(usgaTee);
      return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography variant="bodySmall" color="warning.main">Yardage differs — update existing?</Typography>
          <Button size="small" variant="text" onClick={() => conflict && handleImportTee(usgaTee, conflict)}>Yes</Button>
          <Button size="small" variant="text" onClick={() => setConfirmYardageTeeId(null)}>No</Button>
        </Stack>
      );
    }

    if (isLinked) {
      return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Chip label="Linked" size="small" color="success" variant="outlined" />
          <Button size="small" variant="text" onClick={() => handleImportTee(usgaTee)}>
            Update
          </Button>
        </Stack>
      );
    }

    return (
      <Button size="small" variant="outlined" onClick={() => handleImportTee(usgaTee)}>
        Import
      </Button>
    );
  };

  return (
    <Stack spacing={1}>
      <Typography variant="labelMedium" color="text.secondary">
        {HANDICAP_LABELS.COURSE_ID}: {ghinCourseId}
      </Typography>
      <CourseTeeListView tees={usgaTees} renderAction={isAdmin && course ? renderImportAction : undefined} />
    </Stack>
  );
}
